import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { handleAzureRedirect, isAzureConfigured } from 'modules/auth/api/azureAuth'
import { loginWithAzureToken } from 'modules/auth/api/authService'

const AZURE_USER_TYPE = 'EMPLOYEE'
const DEBUG_AZURE_LOGS = process.env.REACT_APP_DEBUG_AZURE === 'true'

const log = (message: string, ...args: any[]) => {
  if (DEBUG_AZURE_LOGS) console.info(`[Azure][Employee] ${message}`, ...args)
}

export function EmployeeAzureCallbackPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const callbackStartedAt = performance.now()
      log('callback page started')
      if (!(await isAzureConfigured(AZURE_USER_TYPE))) {
        log('configuration check failed before redirect handling')
        setError('Azure configuration is missing.')
        return
      }

      try {
        const redirectStartedAt = performance.now()
        log('handleAzureRedirect started')
        const result = await handleAzureRedirect(AZURE_USER_TYPE)
        log('handleAzureRedirect finished in', Math.round(performance.now() - redirectStartedAt), 'ms')
        if (!result?.idToken) {
          setError('ไม่พบข้อมูลการยืนยันตัวตนจาก Azure')
          return
        }

        const loginStartedAt = performance.now()
        log('loginWithAzureToken started')
        const response = await loginWithAzureToken({
          idToken: result.idToken,
          userType: 'EMPLOYEE',
        })
        log('loginWithAzureToken finished in', Math.round(performance.now() - loginStartedAt), 'ms')

        if (cancelled) return
        log('callback flow completed in', Math.round(performance.now() - callbackStartedAt), 'ms')
        navigate('/modules', { replace: true, state: { user: { ...response, userType: 'employee' } } })
      } catch (err: any) {
        if (cancelled) return
        const message = err?.response?.data ?? err?.message ?? 'ไม่สามารถเข้าสู่ระบบด้วย Azure ได้'
        setError(typeof message === 'string' ? message : 'ไม่สามารถเข้าสู่ระบบด้วย Azure ได้')
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-100 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white/90 shadow-xl border border-blue-100 p-6 text-center">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-red-600">Azure Login Failed</h1>
            <p className="mt-2 text-sm text-red-500">{error}</p>
            <button
              type="button"
              className="mt-4 text-sm text-blue-600 hover:underline"
              onClick={() => navigate('/login/employee')}
            >
              Back to Employee Login
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
            <h1 className="text-lg font-semibold text-blue-900">Signing you in...</h1>
            <p className="mt-2 text-sm text-blue-700">Please wait while we validate your account.</p>
          </>
        )}
      </div>
    </div>
  )
}
